import React from 'react';
import { Box } from '@mui/material';
import { colors, IconButton } from '@sweep-io/sweep-design';
import { Close, ArrowRight } from '@sweep-io/sweep-design/dist/icons';
import { useIsCanvasUnited } from '../../../hooks/useIsCanvasUnited';

export interface InlinePanelProps {
  width: number | string;
  onClose: () => void;
  header: React.ReactNode;
  children: React.ReactNode;
  showHeaderBorder?: boolean;
  sx?: React.ComponentProps<typeof Box>['sx'];
}

/**
 * Base inline panel component.
 * Provides consistent container structure with header and content areas.
 *
 * The close button is absolutely positioned in the top-right corner.
 * Right padding is automatically added to the header area to prevent overlap.
 * Header border is controlled by the `showHeaderBorder` prop.
 *
 * For resizable panels, use ResizableInlinePanel which wraps this component.
 */
export const InlinePanel: React.FC<InlinePanelProps> = ({
  width,
  onClose,
  header,
  children,
  showHeaderBorder = true,
  sx,
}) => {
  const isCanvasUnited = useIsCanvasUnited();

  const widthValue = typeof width === 'number' ? `${width}px` : width;

  return (
    <Box
      sx={{
        width: widthValue,
        height: '100%',
        backgroundColor: colors.white,
        borderLeft: `1px solid ${colors.grey[200]}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        flexShrink: 0,
        ...(isCanvasUnited && {
          borderTop: `1px solid ${colors.grey[200]}`,
          borderRadius: '8px 0 0 0',
          marginTop: '48px',
          height: 'calc(100% - 48px)',
        }),
        ...sx,
      }}
    >
      {/* Close button - absolutely positioned */}
      <Box
        sx={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
        }}
      >
        <IconButton onClick={onClose} size="small" variant="flat">
          {isCanvasUnited ? <ArrowRight /> : <Close />}
        </IconButton>
      </Box>

      {/* Header wrapper - includes right padding for close button and optional border */}
      <Box
        sx={{
          paddingRight: '48px',
          borderBottom: showHeaderBorder ? `1px solid ${colors.grey[200]}` : 'none',
        }}
      >
        {header}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default InlinePanel;
